import { EventType } from "@/analytics/analytics-events";
import {
  ToggleClusterVisibilityProperties,
  ToggleScanVisibilityProperties,
} from "@/analytics/staging-area-workflows-events-properties";
import {
  ElementIcon,
  ElementIconType,
  selectLocationType,
  useElementIcon,
} from "@/components/ui/icons";
import { OverlayBadgeData, useOverlayBadge } from "@/hooks/use-overlay-bagde";
import {
  selectActiveBackgroundTasksInChildren,
  selectBackgroundTaskForIElement,
} from "@/store/background-tasks/background-tasks-selector";
import { selectSearchTagMatches } from "@/store/project-filtering/project-filtering-selectors";
import { selectIsScanVisible } from "@/store/selections-selectors";
import { setHiddenScans, setVisibleScans } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { BackgroundTask } from "@/utils/background-tasks";
import { TreeData, TreeDataElement } from "@/utils/tree-generation/tree-data";
import {
  CircularProgress,
  FaroChipTag,
  FaroIconButton,
  FaroText,
  FaroTooltip,
  NO_TRANSLATE_CLASS,
  useBreakpointMdUp,
  useCheckForOverflow,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElementType,
  IElementTypeHint,
  isIElementSection,
  isIElementSectionDataSession,
} from "@faro-lotv/ielement-types";
import {
  isSingleScanSection,
  selectIElement,
  selectIsSubtreeLoading,
} from "@faro-lotv/project-source";
import { isBackgroundTaskActive } from "@faro-lotv/service-wires";
// eslint-disable-next-line no-restricted-imports -- flat-ui badges cannot show the coloured overlay icon (ST-5160)
import { Badge } from "@mui-wrapper";
import { Box, Stack } from "@mui/material";
import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { NodeApi } from "react-arborist";
import { ContextMenu } from "../tree-context-menu";
import { TreeNode, TreeNodeProps } from "../tree-node";
import { TreePopUp } from "../tree-popup";
import { CaptureTreeAction } from "./capture-tree-action";
import { CaptureTreePreview } from "./capture-tree-preview";
import { selectTreeNodeDisabledReason } from "./capture-tree-node-selectors";

/**
 * Pick the current task for a specific tree node.
 *
 * Eg a task that references the tree node directly
 * or, if the tree node is a section a task relative to one
 * or, if the tree node is a laser scan section and one of the laser scans associated to it has a task
 *
 * @param id the IElement id for this tree node
 * @returns the Task to pair to this tree node
 */
function selectBackgroundTaskForTreeNode(id: GUID) {
  return (state: RootState): BackgroundTask | undefined => {
    const iElement = selectIElement(id)(state);
    if (iElement === undefined) return;

    const task = selectBackgroundTaskForIElement(id)(state);
    if (task) {
      return task;
    }

    if (isIElementSection(iElement)) {
      for (const id of iElement.childrenIds ?? []) {
        const task = selectBackgroundTaskForIElement(id)(state);
        if (task) {
          return task;
        }
      }
    }

    if (isIElementSectionDataSession(iElement)) {
      // There is no task associated to given node and it is a laser scan section,
      // then check if any of the child point clouds are being processed and take the first task
      return selectActiveBackgroundTasksInChildren(id)(state)[0];
    }
  };
}

type CaptureTreeNodeProps = TreeNodeProps<TreeData> & {
  /** Handle to enable dragging of the tree node */
  dragHandle?(el: HTMLDivElement | null): void;
  /** Custom extra elements to be placed inside the NodeLabel */
  extraElements?: ReactNode;
};

/** @returns the component to use for each node in the capture tree. It contains an arrow button to expand the node and a label */
export function CaptureTreeNode({
  node,
  style,
  collapseIcon,
  dragHandle,
  extraElements,
}: CaptureTreeNodeProps): JSX.Element {
  const { hasOverflown, checkForOverflow } = useCheckForOverflow();

  const locationType = useAppSelector(selectLocationType(node.id));

  const icon = useElementIcon(
    node.data.element,
    node.data.directParent,
    locationType,
  );

  const task = useAppSelector(selectBackgroundTaskForTreeNode(node.id));
  const taskInProgress = !!task && isBackgroundTaskActive(task.state);

  const overlayBadgeData = useOverlayBadge(node.id, node.isClosed);

  const disabledReason = useAppSelector(selectTreeNodeDisabledReason(node.id));
  const isDisabled = !!disabledReason;

  // Since react-arborist has no built-in "disabled" state this callback prevents items from being selected
  const onNodeClick = useCallback(
    (ev: MouseEvent) => {
      if (isDisabled) {
        ev.stopPropagation();
      }
    },
    [isDisabled],
  );

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const showTooltip = useCallback(() => setIsPopupOpen(true), []);
  const hideTooltip = useCallback(() => setIsPopupOpen(false), []);

  // The visibility toggle has its own tooltip, so the node tooltip is suppressed while it is hovered
  const [isVisibilityHovered, setIsVisibilityHovered] = useState(false);

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const onToggleContextMenu = useCallback(
    (open: boolean) => {
      setIsContextMenuOpen(open);
      if (isPopupOpen && open) {
        hideTooltip();
      }
    },
    [isPopupOpen, hideTooltip],
  );

  // On smaller screens the tree is in a drawer and the preview wouldn't be visible
  const isLargeScreen = useBreakpointMdUp();
  const hasCapturePreview =
    useAppSelector(selectHasFeature(Features.CaptureHoverPreview)) &&
    isLargeScreen;
  const [isNodeHovered, setIsNodeHovered] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [boxRef, setBoxRef] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    dragHandle?.(boxRef);
  }, [boxRef, dragHandle]);

  return (
    <Box
      ref={setBoxRef}
      component="div"
      style={{ ...style, height: "100%" }}
      onClick={onNodeClick}
      onMouseOver={showTooltip}
      onMouseOut={hideTooltip}
      onPointerEnter={() => setIsNodeHovered(true)}
      onPointerLeave={() => setIsNodeHovered(false)}
    >
      <TreePopUp
        parent={boxRef}
        title={node.data.label}
        isTooltipEnabled={hasOverflown || isDisabled || taskInProgress}
        disabledReason={disabledReason}
        task={task}
        open={isPopupOpen && !isVisibilityHovered && !isPreviewOpen}
      >
        <TreeNode<TreeData> node={node} collapseIcon={collapseIcon}>
          <NodeLabel
            iconType={icon}
            overlayBadgeData={overlayBadgeData}
            name={node.data.label}
            element={node.data.element}
            onMouseEnterName={(ev) => checkForOverflow(ev.currentTarget)}
            activeTask={taskInProgress ? task : undefined}
            onToggleContextMenu={onToggleContextMenu}
            onVisibilityHoverChange={setIsVisibilityHovered}
            isDisabled={isDisabled}
            isSelected={node.isSelected}
            childrenNodes={node.children}
            hasChildTreeNodes={!!node.data.children?.length}
            parentNode={node.parent}
            extraElements={extraElements}
          />
        </TreeNode>
      </TreePopUp>
      {hasCapturePreview && (
        <CaptureTreePreview
          // Folders show nothing, even if they contain captures
          elementId={
            node.data.children?.length ? undefined : node.data.element?.id
          }
          name={node.data.label}
          icon={icon}
          anchorEl={boxRef}
          treeEl={node.tree.listEl.current}
          isNodeHovered={isNodeHovered}
          isDisabled={isContextMenuOpen || isDisabled}
          onOpenChange={setIsPreviewOpen}
        />
      )}
    </Box>
  );
}

type NodeLabelProps = {
  /** Specific Icon to show for the current tree node */
  iconType: ElementIconType;

  /** Type of this IElement */
  element?: TreeData["element"];

  /** Name of the IElement represented by the node */
  name: string;

  /** Contains data necessary to render the overlay icon on the node */
  overlayBadgeData?: OverlayBadgeData;

  /** Method to call when the mouse enters the element containing the name */
  onMouseEnterName(event: React.MouseEvent): void;

  /** a task which is associated to current node and is active or in progress */
  activeTask?: BackgroundTask;

  /** Callback executed when the context menu state changes between open and close */
  onToggleContextMenu(open: boolean): void;

  /** Callback executed when the mouse enters or leaves the visibility toggle */
  onVisibilityHoverChange(isHovered: boolean): void;

  /** True if the node is disabled */
  isDisabled: boolean;

  /** True if the node is selected */
  isSelected: boolean;

  /** The children nodes in the tree */
  childrenNodes: Array<NodeApi<TreeData>> | null;

  /** Whether this node has children that are rendered as their own tree nodes.*/
  hasChildTreeNodes: boolean;

  /** The parent node in the tree */
  parentNode: NodeApi<TreeData> | null;

  /** Custom optional elements to be added inside the label */
  extraElements?: ReactNode;
};

/** @returns the label component used, for each node, in the tree with the node's icon, name and context menu */
function NodeLabel({
  iconType,
  name,
  element,
  overlayBadgeData,
  onMouseEnterName,
  activeTask,
  onToggleContextMenu,
  onVisibilityHoverChange,
  isDisabled,
  isSelected,
  childrenNodes,
  hasChildTreeNodes,
  parentNode,
  extraElements,
}: NodeLabelProps): JSX.Element {
  const dispatch = useAppDispatch();

  // If the user has permission to edit the or not
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const isLoading = useAppSelector((state) =>
    element ? selectIsSubtreeLoading(element.id)(state) : false,
  );

  const iElement = useAppSelector(selectIElement(element?.id));
  const isScan = !!iElement && isSingleScanSection(iElement);
  const isCluster = computeIsCluster(element);

  const isSiteScapeScan =
    element?.typeHint === IElementTypeHint.siteScapeCapture ||
    element?.typeHint === IElementTypeHint.siteScapeScan;

  const isSiteScapeOrPhotogrammetryScan =
    isSiteScapeScan ||
    element?.typeHint === IElementTypeHint.photogrammetryScan;

  const isElementVisible = useAppSelector(
    selectIsScanVisible(element?.id ?? ""),
  );

  // If the node is selected, always set it as visible
  useEffect(() => {
    if (isSelected && !isElementVisible && element) {
      dispatch(setVisibleScans([element.id]));
    }
  }, [isSelected, isElementVisible, dispatch, element]);

  const isHideDisabled = isSelected && isElementVisible;

  const visibilityTooltip = useVisibilityTooltip(
    isHideDisabled,
    isElementVisible,
  );

  const toggleVisibility = useToggleVisibility(
    element,
    childrenNodes,
    parentNode,
    isElementVisible,
  );

  const searchTagMatches = useAppSelector(
    selectSearchTagMatches(element, hasChildTreeNodes),
  );

  return (
    <>
      {/* FIXME: Figure out how to handle PC alignment state without querying the store here */}
      <Badge
        badgeContent={overlayBadgeData?.badgeContent}
        color={
          overlayBadgeData ? overlayBadgeData.badgeBackgroundColor : "warning"
        }
        overlap="circular"
        invisible={!overlayBadgeData}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        {isLoading ? (
          <CircularProgress
            aria-label="project tree loading spinner"
            size="1.125em"
          />
        ) : (
          <ElementIcon icon={iconType} sx={{ fontSize: "1.125em" }} />
        )}
      </Badge>

      <Stack
        direction="row"
        sx={{ alignItems: "center", flexGrow: 1, minWidth: 0, columnGap: 0.5 }}
      >
        <FaroText
          variant="bodyM"
          component="div"
          color="inherit"
          aria-label={name}
          noWrap
          onMouseEnter={onMouseEnterName}
          className={NO_TRANSLATE_CLASS}
          sx={{ ml: 1, fontSize: "0.875em" }}
        >
          {name}
        </FaroText>
        {searchTagMatches.map((tag) => (
          <Box key={tag.id} component="span" sx={{ flexShrink: 0 }}>
            <FaroChipTag
              label={tag.name}
              size="small"
              tooltip={tag.name}
              className={NO_TRANSLATE_CLASS}
            />
          </Box>
        ))}
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: "center", rowGap: 0.75, height: "100%" }}
      >
        {!isDisabled && element && (
          <CaptureTreeAction
            id={element.id}
            type={element.type}
            typeHint={element.typeHint}
          />
        )}
        {activeTask && (
          <CircularProgress
            size={16}
            sx={{ minHeight: "16px", minWidth: "16px", mx: 1 }}
            variant={activeTask.progress > 0 ? "determinate" : "indeterminate"}
            value={activeTask.progress}
          />
        )}
      </Stack>
      {(isScan || isCluster) && !isSiteScapeScan && (
        <Box
          component="span"
          sx={{ display: "flex" }}
          onMouseEnter={() => onVisibilityHoverChange(true)}
          onMouseLeave={() => onVisibilityHoverChange(false)}
        >
          <FaroTooltip title={visibilityTooltip}>
            <FaroIconButton
              aria-label="toggle item visibility"
              onClick={toggleVisibility}
              disabled={isHideDisabled}
              size="xs"
              margin={0.5}
              dark={isSelected}
            >
              <ElementIcon
                icon={
                  isElementVisible
                    ? ElementIconType.Visible
                    : ElementIconType.NonVisible
                }
              />
            </FaroIconButton>
          </FaroTooltip>
        </Box>
      )}
      {element && hasWritePermission && !isSiteScapeOrPhotogrammetryScan && (
        <ContextMenu
          id={element.id}
          isNodeDisabled={isDisabled}
          onToggle={onToggleContextMenu}
        />
      )}
      {extraElements}
    </>
  );
}

type ToggleVisibilityCbk = (ev: React.MouseEvent) => void;

/**
 * @returns the tooltip to show for the visibility toggle of a tree node
 * @param isHideDisabled whether the node can't be hidden because it is the item currently in view
 * @param isElementVisible whether the node is currently visible
 */
function useVisibilityTooltip(
  isHideDisabled: boolean,
  isElementVisible: boolean,
): string {
  return useMemo(() => {
    if (isHideDisabled) return "Can't hide the item currently in view";
    return isElementVisible ? "Hide" : "Show";
  }, [isHideDisabled, isElementVisible]);
}

function useToggleVisibility(
  element: TreeDataElement | undefined,
  childrenNodes: Array<NodeApi<TreeData>> | null,
  parentNode: NodeApi<TreeData> | null,
  isElementVisible: boolean,
): ToggleVisibilityCbk {
  const dispatch = useAppDispatch();

  const hiddenScans = useAppSelector((state) => state.selections.hiddenScans);

  return useCallback(
    (ev: React.MouseEvent) => {
      if (!element) return;

      const isCluster = computeIsCluster(element);
      if (isCluster) {
        Analytics.track<ToggleClusterVisibilityProperties>(
          EventType.toggleClusterVisibility,
          { visible: !isElementVisible, origin: "viewer" },
        );
      } else {
        Analytics.track<ToggleScanVisibilityProperties>(
          EventType.toggleScanVisibility,
          {
            via: "scan tree",
            visible: !isElementVisible,
            origin: "viewer",
          },
        );
      }

      const subTree = new Set<GUID>();
      const stack = [{ currentNodeId: element.id, childrenNodes }];
      while (stack.length > 0) {
        const value = stack.pop();
        if (!value) break;
        const { currentNodeId, childrenNodes } = value;
        subTree.add(currentNodeId);
        if (!childrenNodes) continue;

        for (const child of childrenNodes) {
          // The item currently in view must stay visible, so its subtree is never hidden
          if (isElementVisible && child.isSelected) continue;

          if (child.data.element) {
            stack.push({
              currentNodeId: child.data.element.id,
              childrenNodes: child.children,
            });
          }
          subTree.add(child.id);
        }
      }

      if (isElementVisible) {
        let currentParent = parentNode;
        while (currentParent) {
          const siblings = currentParent.children;
          if (
            siblings &&
            !siblings.some(({ id }) => !hiddenScans[id] && !subTree.has(id))
          ) {
            subTree.add(currentParent.id);
          }
          currentParent = currentParent.parent;
        }
        dispatch(setHiddenScans([...subTree]));
      } else {
        let currentParent = parentNode;
        while (currentParent) {
          subTree.add(currentParent.id);
          currentParent = currentParent.parent;
        }
        dispatch(setVisibleScans([...subTree]));
      }

      ev.stopPropagation();
    },
    [
      childrenNodes,
      dispatch,
      element,
      hiddenScans,
      isElementVisible,
      parentNode,
    ],
  );
}

function computeIsCluster(element: TreeDataElement | undefined): boolean {
  return (
    !!element &&
    ((element.type === IElementType.section &&
      element.typeHint === IElementTypeHint.cluster) ||
      element.type === IElementType.cluster)
  );
}
